import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Clock, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Question {
  id: string;
  text: string;
  type: string;
  scale: string[];
  category: string;
  facet?: string;
  reverse_scored?: boolean;
}

interface Test {
  id: string;
  name: string;
  description: string;
  time_limit_minutes: number;
  questions: Question[];
  scoring_criteria: any;
}

interface ProfessionalSkillsTestProps {
  candidateId: string;
  candidateName: string;
  onComplete: () => void;
  onCancel?: () => void;
}

type TestPhase = 'ipip' | 'complete';

export function ProfessionalSkillsTest({ 
  candidateId, 
  candidateName, 
  onComplete, 
  onCancel 
}: ProfessionalSkillsTestProps) {
  const [currentPhase, setCurrentPhase] = useState<TestPhase>('ipip');
  const [ipipTest, setIpipTest] = useState<Test | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [ipipAnswers, setIpipAnswers] = useState<Record<string, number>>({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [testStarted, setTestStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchTest();
  }, []);

  useEffect(() => {
    if (testStarted && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [testStarted, timeLeft]);

  // Greek translations for IPIP-NEO questions - Fixed and expanded
  const translateIpipQuestion = (englishText: string): string => {
    const translations: Record<string, string> = {
      // Extraversion - Friendliness
      "I am the life of the party.": "Είμαι η ψυχή της παρέας.",
      "I don't talk a lot.": "Δεν μιλάω πολύ.",
      "I feel comfortable around people.": "Νιώθω άνετα με τους ανθρώπους.",
      "I keep in the background.": "Μένω στο παρασκήνιο.",
      
      // Extraversion - Gregariousness
      "I start conversations.": "Ξεκινάω συζητήσεις.",
      "I have little to say.": "Έχω λίγα να πω.",
      "I talk to a lot of different people at parties.": "Μιλάω με πολλούς διαφορετικούς ανθρώπους στα πάρτι.",
      "I don't like to draw attention to myself.": "Δεν μου αρέσει να τραβάω την προσοχή.",
      
      // Extraversion - Assertiveness
      "I don't mind being the center of attention.": "Δε με πειράζει να είμαι το κέντρο της προσοχής.",
      "I am quiet around strangers.": "Είμαι ήσυχος με τους αγνώστους.",
      "I take charge.": "Αναλαμβάνω την ευθύνη.",
      "I wait for others to lead the way.": "Περιμένω τους άλλους να δείξουν το δρόμο.",
      
      // Extraversion - Activity Level
      "I am always busy.": "Είμαι πάντα απασχολημένος.",
      "I am less active than other people.": "Είμαι λιγότερο δραστήριος από άλλους ανθρώπους.",
      "I do a lot in my spare time.": "Κάνω πολλά στον ελεύθερό μου χρόνο.",
      "I like to take it easy.": "Μου αρέσει να το παίρνω χαλαρά.",
      
      // Extraversion - Excitement Seeking
      "I love excitement.": "Αγαπάω τον ενθουσιασμό.",
      "I prefer routine to variety.": "Προτιμώ τη ρουτίνα από την ποικιλία.",
      "I enjoy wild flights of fantasy.": "Απολαμβάνω τα άγρια ταξίδια της φαντασίας.",
      "I dislike loud music.": "Δεν μου αρέσει η δυνατή μουσική.",
      
      // Extraversion - Cheerfulness
      "I feel comfortable with myself.": "Νιώθω άνετα με τον εαυτό μου.",
      "I am not a cheerful optimist.": "Δεν είμαι ένας χαρούμενος αισιόδοξος.",
      "I radiate joy.": "Ακτινοβολώ χαρά.",
      "I seldom joke around.": "Σπάνια κάνω αστεία.",
      
      // Agreeableness - Trust
      "I feel little concern for others.": "Δείχνω λίγη ανησυχία για τους άλλους.",
      "I am interested in people.": "Ενδιαφέρομαι για τους ανθρώπους.",
      "I insult people.": "Προσβάλλω τους ανθρώπους.",
      "I sympathize with others' feelings.": "Συμπάσχω με τα συναισθήματα των άλλων.",
      "I am not interested in other people's problems.": "Δεν ενδιαφέρομαι για τα προβλήματα των άλλων.",
      "I have a soft heart.": "Έχω μαλακή καρδιά.",
      "I am not really interested in others.": "Δεν ενδιαφέρομαι πραγματικά για τους άλλους.",
      "I take time out for others.": "Βρίσκω χρόνο για τους άλλους.",
      "I feel others' emotions.": "Νιώθω τα συναισθήματα των άλλων.",
      "I make people feel at ease.": "Κάνω τους ανθρώπους να νιώθουν άνετα.",
      
      // Agreeableness - Trust continued
      "I trust others.": "Εμπιστεύομαι τους άλλους.",
      "I suspect hidden motives in others.": "Υποπτεύομαι κρυφά κίνητρα στους άλλους.",
      "I believe that others have good intentions.": "Πιστεύω ότι οι άλλοι έχουν καλές προθέσεις.",
      "I distrust people.": "Δυσπιστώ προς τους ανθρώπους.",
      
      // Conscientiousness
      "I am always prepared.": "Είμαι πάντα προετοιμασμένος.",
      "I leave my belongings around.": "Αφήνω τα πράγματά μου παντού.",
      "I pay attention to details.": "Δίνω προσοχή στις λεπτομέρειες.",
      "I make a mess of things.": "Κάνω μπέρδεμα με τα πράγματα.",
      "I get chores done right away.": "Κάνω τις δουλειές αμέσως.",
      "I often forget to put things back in their proper place.": "Συχνά ξεχνάω να βάλω τα πράγματα στη θέση τους.",
      "I like order.": "Μου αρέσει η τάξη.",
      "I shirk my duties.": "Αποφεύγω τα καθήκοντά μου.",
      "I follow a schedule.": "Ακολουθώ ένα πρόγραμμα.",
      "I am exacting in my work.": "Είμαι ακριβής στη δουλειά μου.",
      
      // Neuroticism
      "I get stressed out easily.": "Αγχώνομαι εύκολα.",
      "I am relaxed most of the time.": "Είμαι χαλαρός τις περισσότερες φορές.",
      "I worry about things.": "Ανησυχώ για τα πράγματα.",
      "I seldom feel blue.": "Σπάνια νιώθω μελαγχολικός.",
      "I am easily disturbed.": "Ενοχλούμαι εύκολα.",
      "I get upset easily.": "Στεναχωριέμαι εύκολα.",
      "I change my mood a lot.": "Αλλάζω διάθεση συχνά.",
      "I have frequent mood swings.": "Έχω συχνές αλλαγές διάθεσης.",
      "I get irritated easily.": "Ερεθίζομαι εύκολα.",
      "I often feel blue.": "Συχνά νιώθω μελαγχολικός.",
      
      // Openness
      "I have a rich vocabulary.": "Έχω πλούσιο λεξιλόγιο.",
      "I have difficulty understanding abstract ideas.": "Δυσκολεύομαι να κατανοήσω αφηρημένες ιδέες.",
      "I have a vivid imagination.": "Έχω ζωηρή φαντασία.",
      "I am not interested in abstract ideas.": "Δεν ενδιαφέρομαι για αφηρημένες ιδέες.",
      "I have excellent ideas.": "Έχω εξαιρετικές ιδέες.",
      "I do not have a good imagination.": "Δεν έχω καλή φαντασία.",
      "I am quick to understand things.": "Καταλαβαίνω τα πράγματα γρήγορα.",
      "I use difficult words.": "Χρησιμοποιώ δύσκολες λέξεις.",
      "I spend time reflecting on things.": "Περνάω χρόνο σκεπτόμενος τα πράγματα.",
      "I am full of ideas.": "Είμαι γεμάτος ιδέες.",
      
      // Additional commonly used questions
      "I complete tasks successfully.": "Ολοκληρώνω τα καθήκοντα με επιτυχία.",
      "I waste my time.": "Σπαταλάω τον χρόνο μου.",
      "I find it difficult to get down to work.": "Δυσκολεύομαι να ξεκινήσω τη δουλειά.",
      "I do things according to a plan.": "Κάνω τα πράγματα σύμφωνα με ένα σχέδιο.",
      "I rush into things.": "Ορμάω στα πράγματα.",
      "I find myself in the same kinds of trouble time and again.": "Βρίσκομαι στα ίδια προβλήματα ξανά και ξανά.",
      "I try to be courteous to everyone I meet.": "Προσπαθώ να είμαι ευγενικός με όλους που συναντάω.",
      "I believe that people are basically moral.": "Πιστεύω ότι οι άνθρωποι είναι βασικά ηθικοί.",
      "I think of myself as a charitable person.": "Θεωρώ τον εαυτό μου φιλανθρωπικό άτομο."
    };
    
    // Return Greek translation if available, otherwise return original text
    const greekTranslation = translations[englishText];
    console.log(`Translating: "${englishText}" -> "${greekTranslation || englishText}"`);
    return greekTranslation || englishText;
  };

  const fetchTest = async () => {
    // FORCE FRESH RELOAD - Clear all state first
    console.log('=== FORCE CLEARING ALL STATE ===');
    setIpipTest(null);
    setIpipAnswers({});
    setCurrentQuestionIndex(0);
    setTestStarted(false);
    setTestResults(null);
    setCurrentPhase('ipip');
    
    setLoading(true);
    try {
      console.log('Fetching IPIP test...');
      
      const { count, error: countError } = await supabase
        .from('ipip_neo_questions')
        .select('*', { count: 'exact', head: true });

      if (countError) {
        console.error('Count error:', countError);
        throw new Error('Η βάση δεδομένων δεν είναι διαθέσιμη');
      }

      console.log('Total IPIP questions available:', count);
      
      if (!count || count === 0) {
        throw new Error('Δεν υπάρχουν διαθέσιμες ερωτήσεις στη βάση δεδομένων');
      }

      const { data: ipipQuestions, error: ipipError } = await supabase
        .from('ipip_neo_questions')
        .select('id, question_number, question_text, factor, facet, reverse_scored')
        .order('question_number');

      console.log('IPIP questions fetched:', ipipQuestions?.length);
      
      if (ipipError) {
        console.error('IPIP fetch error:', ipipError);
        throw new Error('Σφάλμα κατά την ανάκτηση των ερωτήσεων');
      }

      if (!ipipQuestions || ipipQuestions.length === 0) {
        throw new Error('Δεν βρέθηκαν ερωτήσεις IPIP-NEO');
      }

      // Convert IPIP-NEO questions to test format
      const formattedQuestions = ipipQuestions.map(q => ({
        id: q.id,
        text: q.question_text, // Now contains only Greek text after cleanup
        type: 'ipip_neo',
        scale: ['Διαφωνώ Απόλυτα', 'Διαφωνώ', 'Ούτε συμφωνώ/ούτε διαφωνώ', 'Συμφωνώ', 'Συμφωνώ Απόλυτα'],
        category: q.factor,
        facet: q.facet,
        reverse_scored: q.reverse_scored
      }));

      const ipipTestData = {
        id: 'ipip-neo-test',
        name: 'IPIP-NEO Αξιολόγηση Προσωπικότητας',
        description: `Αξιολόγηση προσωπικότητας Big Five με ${ipipQuestions.length} ερωτήσεις`,
        time_limit_minutes: 45,
        questions: formattedQuestions,
        scoring_criteria: {}
      };
      
      setIpipTest(ipipTestData);
      setTimeLeft(45 * 60);
      console.log('IPIP test loaded successfully with', formattedQuestions.length, 'questions');

    } catch (error: any) {
      console.error('Full error:', error);
      toast({
        title: "Σφάλμα",
        description: error.message || "Δεν ήταν δυνατή η φόρτωση του τεστ",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const startTest = async () => {
    if (!ipipTest) return;
    
    try {
      const { error } = await supabase
        .from('test_results')
        .insert({
          candidate_id: candidateId,
          test_id: ipipTest.id,
          status: 'in_progress',
          started_at: new Date().toISOString()
        } as any);

      if (error) throw error;
      setTestStarted(true);
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message || "Δεν ήταν δυνατή η φόρτωση του τεστ",
        variant: "destructive",
      });
    }
  };

  const handleAnswer = (questionId: string, answerIndex: number) => {
    console.log('🔥 handleAnswer called!');
    console.log('🔥 Question ID:', questionId);
    console.log('🔥 Answer Index:', answerIndex);
    console.log('🔥 Previous answers:', ipipAnswers);
    
    setIpipAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionId]: answerIndex
      };
      console.log('🔥 New answers:', newAnswers);
      return newAnswers;
    });
    
    console.log('🔥 State update triggered!');
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < ipipTest!.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleTestComplete();
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const calculateIPIPScores = (answers: Record<string, number>) => {
    if (!ipipTest) return {};

    const factors = ['Extraversion', 'Agreeableness', 'Conscientiousness', 'Neuroticism', 'Openness'];
    const factorScores: Record<string, number> = {};

    factors.forEach(factor => {
      const factorQuestions = ipipTest.questions?.filter(q => q.category === factor) || [];
      if (factorQuestions.length > 0) {
        let totalScore = 0;
        factorQuestions.forEach(q => {
          const answer = answers[q.id] !== undefined ? answers[q.id] : 2;
          const score = q.reverse_scored ? 4 - answer : answer;
          totalScore += score;
        });
        
        const avgScore = totalScore / factorQuestions.length;
        factorScores[factor] = Math.round((avgScore / 4) * 100);
      } else {
        factorScores[factor] = 50;
      }
    });

    const overallScore = Math.round(Object.values(factorScores).reduce((a, b) => a + b, 0) / 5);
    
    let recommendation = "";
    let suitability = "";

    if (overallScore >= 85) {
      recommendation = "Εξαιρετικό προφίλ προσωπικότητας για θέσεις ασφαλείας. Άριστη ψυχολογική σταθερότητα και καταλληλότητα.";
      suitability = "excellent";
    } else if (overallScore >= 75) {
      recommendation = "Καλό προφίλ προσωπικότητας. Κατάλληλος για θέσεις ασφαλείας με συνηθισμένη επίβλεψη.";
      suitability = "good";
    } else if (overallScore >= 60) {
      recommendation = "Μέτριο προφίλ προσωπικότητας. Κατάλληλος για θέσεις ασφαλείας με κατάλληλη υποστήριξη και εκπαίδευση.";
      suitability = "conditional";
    } else {
      recommendation = "Το προφίλ προσωπικότητας παρουσιάζει περιοχές ανησυχίας για θέσεις ασφαλείας. Απαιτείται περαιτέρω αξιολόγηση.";
      suitability = "needs_review";
    }

    return {
      categoryScores: factorScores,
      overallScore,
      recommendation,
      suitability
    };
  };

  const handleTestComplete = async () => {
    if (!ipipTest) return;
    
    setLoading(true);
    try {
      const scores = calculateIPIPScores(ipipAnswers);
      const completionTime = Math.round((ipipTest.time_limit_minutes * 60 - timeLeft) / 60);

      const { error } = await supabase
        .from('test_results')
        .update({
          answers: ipipAnswers,
          scores: scores,
          completion_time: completionTime,
          completed_at: new Date().toISOString(),
          status: 'completed',
          personality_profile: scores.categoryScores,
          recommendations: scores.recommendation
        })
        .eq('candidate_id', candidateId)
        .eq('test_id', ipipTest.id)
        .eq('status', 'in_progress');

      if (error) throw error;

      await supabase
        .from('candidates')
        .update({ 
          status: 'tested',
          notes: scores.recommendation
        })
        .eq('id', candidateId);

      setTestResults(scores);
      setCurrentPhase('complete');

      toast({
        title: "Επιτυχία",
        description: "Το τεστ ολοκληρώθηκε με επιτυχία",
      });

    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTimeUp = () => {
    toast({
      title: "Τέλος χρόνου",
      description: "Ο χρόνος του τεστ έληξε",
      variant: "destructive",
    });
    handleTestComplete();
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getProgress = () => {
    if (!ipipTest) return 0;
    return ((currentQuestionIndex + 1) / ipipTest.questions.length) * 100;
  };

  if (!ipipTest) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-6">
          <div className="text-center">Φόρτωση τεστ...</div>
        </CardContent>
      </Card>
    );
  }

  if (currentPhase === 'complete') {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="text-center text-2xl text-primary">
            🎉 Ολοκλήρωση Ψυχομετρικής Αξιολόγησης
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <div className="text-6xl font-bold text-primary mb-2">
              {testResults?.overallScore}/100
            </div>
            <div className="text-lg text-muted-foreground">
              Συνολική Βαθμολογία Προσωπικότητας
            </div>
          </div>

          <div className="bg-muted p-6 rounded-lg">
            <h3 className="font-semibold mb-4 text-lg">Αποτελέσματα IPIP-NEO:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {testResults?.categoryScores && Object.entries(testResults.categoryScores).map(([factor, score]) => (
                <div key={factor} className="bg-white p-3 rounded">
                  <div className="text-sm font-medium">{factor}</div>
                  <div className="text-lg font-bold text-primary">{score as number}/100</div>
                </div>
              ))}
            </div>
            <div className="text-sm leading-relaxed">
              {testResults?.recommendation}
            </div>
          </div>

          <div className={`p-4 rounded-lg border-l-4 ${
            testResults?.suitability === 'excellent' ? 'bg-green-50 border-green-500 text-green-900' :
            testResults?.suitability === 'good' ? 'bg-blue-50 border-blue-500 text-blue-900' :
            testResults?.suitability === 'conditional' ? 'bg-yellow-50 border-yellow-500 text-yellow-900' :
            'bg-red-50 border-red-500 text-red-900'
          }`}>
            <h4 className="font-semibold mb-2">Σύσταση:</h4>
            <p className="text-sm leading-relaxed">
              {testResults?.recommendation}
            </p>
          </div>

          <div className="flex justify-center">
            <Button onClick={onComplete} size="lg">
              Ολοκλήρωση Αξιολόγησης
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!testStarted) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Ψυχομετρική Αξιολόγηση Προσωπικότητας (IPIP-NEO)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            Ψυχολογική αξιολόγηση προσωπικότητας με βάση το μοντέλο Big Five με {ipipTest.questions.length} ερωτήσεις
          </p>
          
          <div className="bg-muted p-4 rounded-lg space-y-3">
            <div className="font-medium text-primary mb-2">Περιγραφή Τεστ:</div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <span className="text-sm">IPIP-NEO Προσωπικότητα ({ipipTest.time_limit_minutes} λεπτά, {ipipTest.questions.length} ερωτήσεις)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <span className="text-sm">Αξιολόγηση & Σύσταση</span>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-3 pt-3 border-t">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Συνολικός Χρόνος: {ipipTest.time_limit_minutes} λεπτά</span>
            </div>
          </div>

          <div className="border-l-4 border-primary pl-4">
            <h4 className="font-medium mb-2">Οδηγίες:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Διαβάστε προσεκτικά κάθε ερώτηση</li>
              <li>• Απαντήστε όσο πιο ειλικρινά μπορείτε</li>
              <li>• Δεν υπάρχουν "σωστές" ή "λάθος" απαντήσεις</li>
              <li>• Επιλέξτε την απάντηση που σας περιγράφει καλύτερα</li>
            </ul>
          </div>

          <div className="flex justify-between">
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Ακύρωση
              </Button>
            )}
            <Button onClick={startTest} className="ml-auto">
              Έναρξη Τεστ
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentQuestion = ipipTest.questions[currentQuestionIndex];
  const currentAnswer = ipipAnswers[currentQuestion.id];

  console.log('=== CURRENT QUESTION DEBUG ===');
  console.log('Current question:', currentQuestion);
  console.log('Question ID:', currentQuestion.id);
  console.log('Question text:', currentQuestion.text);
  console.log('Current answer:', currentAnswer);
  console.log('All answers:', ipipAnswers);
  console.log('===============================');

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>
            IPIP-NEO: Ερώτηση {currentQuestionIndex + 1} από {ipipTest.questions.length}
          </CardTitle>
          <div className="flex items-center gap-2 text-primary font-mono">
            <Clock className="w-4 h-4" />
            {formatTime(timeLeft)}
          </div>
        </div>
        <div className="text-sm text-muted-foreground mb-2">
          Αξιολόγηση Προσωπικότητας
        </div>
        <Progress value={getProgress()} className="mt-2" />
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="text-lg font-medium leading-relaxed">
          {currentQuestion.text}
        </div>

        <div className="flex flex-wrap gap-3 justify-center">
          {currentQuestion.scale.map((option, index) => {
            const isSelected = currentAnswer === index;
            
            return (
              <button 
                key={index}
                type="button"
                onClick={() => handleAnswer(currentQuestion.id, index)}
                className={`
                  px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm
                  ${isSelected 
                    ? 'border-primary bg-primary text-primary-foreground shadow-md' 
                    : 'border-gray-200 bg-white hover:border-primary/50 hover:bg-primary/5'
                  }
                  min-w-[120px] flex-1 max-w-[200px]
                `}
              >
                {option}
              </button>
            );
          })}
        </div>

        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={goToPreviousQuestion}
            disabled={currentQuestionIndex === 0}
          >
            Προηγούμενη
          </Button>
          
          <Button
            onClick={goToNextQuestion}
            disabled={currentAnswer === undefined || loading}
          >
            {loading 
              ? "Αποθήκευση..." 
              : currentQuestionIndex === ipipTest.questions.length - 1 
                ? "Ολοκλήρωση Τεστ"
                : "Επόμενη"
            }
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
